import React, { FC, useEffect, useRef, useState, useCallback } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList } from 'react-window'
import useTitle from 'react-use/lib/useTitle'

import Splash from '../../components/Splash'
import Pager from '../../components/Pager'
import { ICursoryOrder } from '../../types/orders'
import { IBranch, IAccount, IUser } from '../../types/user'
import withAccountAndBranch from '../../common/withAccountAndBranch'
import * as storage from '../../common/storage'
import {
  FilterOrdersReq,
  IOrdersList,
  IOrdersFilter,
  requestAllOrders,
} from './ordersActions'
import OrdersPlaceholder from './OrdersPlaceholder'
import useThunkDispatch from '../../common/useThunkDispatch'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import OrdersRow from './OrdersRow'
import OrdersFilter from './OrdersFilter'
import { FILTER_STORAGE_KEY } from './OrdersFilter'

interface IOrdersProps {
  user: IUser
  account?: IAccount
  branch?: IBranch
}

const PER_PAGE = 25

const Orders: FC<IOrdersProps> = ({
  user,
  account: currentAccount,
  branch,
}) => {
  useTitle('Orders - Forshaw')
  const dispatch = useThunkDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const storageFilters = JSON.parse(
    storage.get(FILTER_STORAGE_KEY, '{"scope":"manageable"}'),
  )
  const [filter, setFilter] = useState<IOrdersFilter>(storageFilters)
  const [list, setList] = useState<IOrdersList>({
    orders: [],
    paginationState: {
      endCursor: '',
      startCursor: '',
      hasNextPage: false,
      hasPreviousPage: false,
    },
    total: 0,
  })
  const [page, setPage] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const listRef = useRef<FixedSizeList>(null)
  const nothingFound = !list.orders.length && !loading
  const listStyles = { height: 'calc(100vh - 300px)' }
  const account = currentAccount || user.account
  const gmFlow = account && account.orderApproveFlow

  const ordersEnabled = account?.ordersEnabled ?? true

  // load orders call
  const loadOrders = async (req: FilterOrdersReq) => {
    setLoading(true)
    if (listRef.current) listRef.current.scrollToItem(0)
    const list = (await dispatch(
      requestAllOrders({
        ...req,
        branchId: (branch && branch.id) || undefined,
        accountId: (account && account.id) || undefined,
      }),
    )) as any
    setList(list)
    setLoading(false)
  }

  // previous page
  const handlePrevious = useCallback(() => {
    loadOrders({
      last: PER_PAGE,
      before: list.paginationState.startCursor,
      ...filter,
    })
    setPage(page - 1)
  }, [list, filter, page])

  // next page
  const handleNext = useCallback(() => {
    loadOrders({
      first: PER_PAGE,
      after: list.paginationState.endCursor,
      ...filter,
    })
    setPage(page + 1)
  }, [list, filter, page])

  // on filter change
  const handleFilterChange = (term: string, filterOption: IOrdersFilter) => {
    setFilter({ ...filter, text: term, ...filterOption })
  }

  // initial load
  useEffect(() => {
    if (branch) {
      loadOrders({ first: PER_PAGE, ...filter })
    }
  }, [filter, branch && branch.id, location.key])

  if (!branch) return <Splash isLoading={true} />

  const branchSelected = branch && branch.id > 0
  const allBranches = !branchSelected
  const multiCatalogEnabled = (account?.erpCatalogs || []).length > 1

  // Users should not see orders page if they can see new replenishment plan
  if (!ordersEnabled) {
    const newLocation = location.pathname.replace('/orders', '/inventory')

    return <Navigate to={newLocation} replace />
  }

  return (
    <div className="flexible-container">
      <OrdersFilter
        onChange={handleFilterChange}
        branchSelected={branchSelected}
        account={account}
      />

      {!nothingFound && (
        <div className="table">
          <div className="table_head-tr">
            <div className="table_td table_title -g20">Submitted on</div>
            {allBranches && (
              <div className="table_td table_title -g25">Branch</div>
            )}
            <div className="table_td table_title -g25">Last Updated by</div>
            <div className="table_td table_title -g20">Status</div>
            {multiCatalogEnabled && (
              <div className="table_td table_title -g20">Distributor</div>
            )}
            <div className="table_td table_title -g20">Sales Order #</div>
            <div className="table_td table_title -g20">PO #</div>
            <div className="table_td table_title -g20">Total</div>
            <div className="table_td table_title -g25">Shipping Info</div>
          </div>
        </div>
      )}

      <div className="table_loading-wrapper">
        {nothingFound ? (
          <OrdersPlaceholder
            message={'No orders submitted this month'}
            style={listStyles}
          />
        ) : (
          <AutoSizer className="table" style={listStyles}>
            {(size) => (
              <FixedSizeList
                ref={listRef}
                className="wonder-scroll"
                {...size}
                itemData={{
                  orders: list.orders,
                  onClick: (order: ICursoryOrder) =>
                    navigate(`${location.pathname}/${order.id}`),
                  allBranches,
                  multiCatalogEnabled,
                  gmFlow,
                  erpOrders: account!.erpOrders,
                }}
                itemCount={list.orders.length}
                itemSize={55 + 17}
                itemKey={(index) => list.orders[index].id}
              >
                {OrdersRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        )}
        <Splash isLoading={loading} bgColor="#F2F6FA" />
      </div>

      {!nothingFound && (
        <Pager
          page={page}
          total={list.total}
          perPage={PER_PAGE}
          previousEnabled={list.paginationState.hasPreviousPage && !loading}
          nextEnabled={list.paginationState.hasNextPage && !loading}
          onNext={handleNext}
          onPrevious={handlePrevious}
          className="-right"
        />
      )}
    </div>
  )
}

export default withAccountAndBranch(Orders)
